<template>
  <div class="flex flex-col">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        Manage SIMPO consumer credentials used by the integration service.
      </p>
      <button type="button" class="btn shrink-0" :disabled="loading" @click="loadCredentials">
        <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
        <span>Refresh</span>
      </button>
    </div>

    <div v-if="loading && !loaded" class="w-full bg-gray-200 rounded-full h-1 overflow-hidden mb-4">
      <div class="bg-primary h-1 rounded-full animate-pulse" style="width: 100%"></div>
    </div>

    <form class="max-w-lg space-y-4" @submit.prevent="saveCredentials">
      <div>
        <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Consumer key</label>
        <input
          v-model="form.consumerKey"
          type="text"
          autocomplete="off"
          placeholder="Enter consumer key"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 mt-1"
          :disabled="loading || saving"
          required
        />
      </div>
      <div>
        <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Consumer secret</label>
        <div class="relative mt-1">
          <input
            v-model="form.consumerSecret"
            :type="showSecret ? 'text' : 'password'"
            autocomplete="new-password"
            placeholder="Enter consumer secret"
            class="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
            :disabled="loading || saving"
            required
          />
          <button
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
            :title="showSecret ? 'Hide secret' : 'Show secret'"
            @click="showSecret = !showSecret"
          >
            <i :class="showSecret ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
          </button>
        </div>
      </div>
      <div class="flex flex-wrap gap-2 pt-1">
        <button type="submit" class="btn-primary" :disabled="loading || saving">
          <i v-if="saving" class="fa fa-spinner fa-spin"></i>
          <i v-else class="fa-solid fa-save"></i>
          <span>{{ credentialsConfigured ? "Update credentials" : "Save credentials" }}</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useToast } from "@/composables";
import { extractErrorMessage, fetchSimpoCredentials, saveSimpoCredentials } from "./simpoApi";

const { successToast, errorToast, warningToast } = useToast();

const loading = ref(false);
const saving = ref(false);
const loaded = ref(false);
const credentialsConfigured = ref(false);
const showSecret = ref(false);
const form = ref({
  consumerKey: "",
  consumerSecret: "",
});

const loadCredentials = async () => {
  loading.value = true;
  try {
    const result = await fetchSimpoCredentials();
    credentialsConfigured.value = result.configured;
    form.value.consumerKey = result.consumerKey || "";
    form.value.consumerSecret = result.consumerSecret || "";
    loaded.value = true;
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } };
    if (err.response?.status === 404) {
      credentialsConfigured.value = false;
      form.value = { consumerKey: "", consumerSecret: "" };
      loaded.value = true;
      return;
    }
    errorToast("Error", extractErrorMessage(error, "Could not load SIMPO credentials"));
  } finally {
    loading.value = false;
  }
};

const saveCredentials = async () => {
  const consumerKey = form.value.consumerKey.trim();
  const consumerSecret = form.value.consumerSecret.trim();
  if (!consumerKey) {
    warningToast("Validation", "Enter a consumer key");
    return;
  }
  if (!consumerSecret) {
    warningToast("Validation", "Enter a consumer secret");
    return;
  }
  saving.value = true;
  try {
    await saveSimpoCredentials({ consumerKey, consumerSecret });
    successToast("Success", "SIMPO credentials saved");
    await loadCredentials();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not save SIMPO credentials"));
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  loadCredentials();
});

defineExpose({ refresh: loadCredentials });
</script>
